import React from 'react';
import ClientsSection from './ClientsSection';
import { sanitizeLinkedContacts } from '../../ui/modals/LinkedContactsModal';
import { getContactKey } from '../../../utils/helpers';
import FinancingToggle from './FinancingToggle';
import { Input, FieldLabel } from '../../ui/primitives';
import CurrencyInput from '../../ui/CurrencyInput';
import { resolvePrefillForSide } from '../../../utils/startDealPrefill';
import { shouldShowFinancing, isFinancingChoiceRequired, FINANCING_TYPES } from '../../../utils/financing';
import { REFERRAL_TYPE_STATUSES, isReferralType, REFERRED_STATUS, statusOptionsForType, defaultStatusForType, transactionTypeLabel } from '../../../config/statuses';
import { sanitizeWorkspaceDataEntrySettings } from '../../../config/workspaceDefaults';
import useMediaQuery from '../../../hooks/useMediaQuery';

const MAX_CLIENT_HELPERS = 5;
const TYPE_OPTIONS = ['Seller', 'Buyer', 'Leasing/Rental', 'Referral'];

function StartTransactionModal({
    statuses = [],
    workspaceDefaults = {},
    contacts = [],
    onUpdateContact,
    onCreateContact,
    onSyncContactFromGoogle,
    applyReviewedSync,
    applyResolvedSync,
    onSearchGoogleContacts,
    workspaceId,
    user,
    onCancel,
    onStartTransaction,
    prefill,
}) {
    const isMobile = useMediaQuery('(max-width: 767px)');
    const resolvedDefaults = React.useMemo(
        () => sanitizeWorkspaceDataEntrySettings(workspaceDefaults),
        [workspaceDefaults]
    );

    const sanitizeClientHelpers = React.useCallback(
        (helpers) => sanitizeLinkedContacts(Array.isArray(helpers) ? helpers : [], { maxItems: MAX_CLIENT_HELPERS }),
        []
    );

    const mapClientWithId = React.useCallback((client = {}) => {
        const isEntity = client.kind === 'entity';
        const since = (client.since || '').trim();
        if (isEntity) {
            const businessName = (client.businessName || '').trim();
            const businessType = (client.businessType || '').trim();
            const repsSource = Array.isArray(client.representatives)
                ? client.representatives
                : (client.contactPerson ? [client.contactPerson] : []);
            const representatives = repsSource
                .map(rep => ({
                    firstName: (rep?.firstName || '').trim(),
                    middleName: (rep?.middleName || '').trim(),
                    lastName: (rep?.lastName || '').trim(),
                    title: (rep?.title || '').trim(),
                    email: (rep?.email || '').trim(),
                    phone: (rep?.phone || '').trim(),
                    googleResourceName: rep?.googleResourceName || null,
                    googleEtag: rep?.googleEtag || null,
                    googleLastUpdated: rep?.googleLastUpdated || null,
                    syncSource: rep?.syncSource || (rep?.googleResourceName ? 'google' : 'manual')
                }))
                .filter(rep => rep.firstName || rep.lastName || rep.email || rep.phone);
            const primary = representatives[0] || {};
            const hasValues = Boolean(businessName || representatives.length);
            const keySeed = { firstName: businessName, lastName: businessType, email: primary.email || '' };
            const existingId = client.contactId || client.id || (hasValues ? getContactKey(keySeed) : '');
            return { id: existingId, contactId: existingId, kind: 'entity', businessName, businessType, representatives, since, syncSource: client.syncSource || 'manual' };
        }
        const firstName = (client.firstName || '').trim();
        const lastName = (client.lastName || '').trim();
        const email = (client.email || '').trim();
        const phone = (client.phone || '').trim();
        const hasValues = Boolean(firstName || lastName || email || phone);
        const existingId = client.contactId || client.id || (hasValues ? getContactKey({ firstName, lastName, email, phone }) : '');
        const googleResourceName = client.googleResourceName || null;
        const syncSource = client.syncSource || (googleResourceName ? 'google' : 'manual');
        const helpers = sanitizeClientHelpers(client.helpers);
        return { id: existingId, contactId: existingId, kind: 'individual', firstName, lastName, email, phone, since, googleResourceName, googleEtag: client.googleEtag || null, googleLastUpdated: client.googleLastUpdated || null, syncSource, helpers };
    }, [sanitizeClientHelpers]);

    // D-07 (39-15): manual archive is gone -- every non-Uncategorized status
    // is a valid option when starting a new transaction. D-13/PIPE-07: Referred
    // and the legacy Prospect value are never offered here either — new deals
    // never start on a status this modal doesn't itself set.
    const activeStatuses = React.useMemo(
        () => statusOptionsForType(null, statuses),
        [statuses]
    );

    const initialStatus = React.useMemo(() => {
        const def = defaultStatusForType(null, resolvedDefaults.defaultTransactionStatus);
        return activeStatuses.includes(def) ? def : (activeStatuses[0] || '');
    }, [resolvedDefaults.defaultTransactionStatus, activeStatuses]);

    const [transactionType, setTransactionType] = React.useState(
        () => (prefill && prefill.transactionType) || resolvedDefaults.defaultTransactionType || 'Buyer'
    );
    const [status, setStatus] = React.useState(initialStatus);

    const statusOptions = React.useMemo(
        () => statusOptionsForType(transactionType, statuses),
        [transactionType, statuses]
    );

    React.useEffect(() => {
        if (isReferralType(transactionType)) {
            if (!REFERRAL_TYPE_STATUSES.includes(status)) setStatus(REFERRED_STATUS);
        } else if (!activeStatuses.includes(status)) {
            setStatus(initialStatus);
        }
    }, [transactionType, status, activeStatuses, initialStatus]);

    // Start-a-deal prefill (Phase 55, D-20 and D-21): the contact is the client,
    // and co-clients from the same migrated Prospect deal start checked.
    const [clients, setClients] = React.useState(() => (
        prefill ? [...prefill.clients, ...(prefill.coClients || []).map((c) => c.client)] : []
    ));
    // Co-clients can arrive after the first render (the contacts lease warms up
    // once the dialog opens). Merge each newly seen one in, checked, without
    // remounting, so nothing the user already set is lost, and never re-add one
    // the user has already seen (and possibly unchecked).
    const seenCoClientIdsRef = React.useRef(null);
    if (seenCoClientIdsRef.current === null) {
        seenCoClientIdsRef.current = new Set(((prefill && prefill.coClients) || []).map((co) => co.client.contactId));
    }
    const coClients = prefill ? prefill.coClients : undefined;
    React.useEffect(() => {
        const fresh = (coClients || []).filter((co) => !seenCoClientIdsRef.current.has(co.client.contactId));
        if (fresh.length === 0) return;
        fresh.forEach((co) => seenCoClientIdsRef.current.add(co.client.contactId));
        setClients((prev) => [
            ...prev,
            ...fresh.map((co) => co.client).filter((c) => !prev.some((p) => p.contactId === c.contactId)),
        ]);
    }, [coClients]);
    const [financingType, setFinancingType] = React.useState(() => (prefill && prefill.financingType) || '');
    const [budget, setBudget] = React.useState(() => (prefill && prefill.budget != null ? prefill.budget : ''));
    const [propertyStreet, setPropertyStreet] = React.useState(() => (prefill && prefill.propertyStreet) || '');
    const [propertyCityStateZip, setPropertyCityStateZip] = React.useState(() => (prefill && prefill.propertyCityStateZip) || '');
    const [listPrice, setListPrice] = React.useState(() => (prefill && prefill.listPrice != null ? prefill.listPrice : ''));
    const [activePrefill, setActivePrefill] = React.useState(prefill || null);
    const needsSidePick = Boolean(prefill && prefill.sides && prefill.sides.length === 2 && !activePrefill.side);
    const prefillName = prefill
        ? ([prefill.contact && prefill.contact.firstName, prefill.contact && prefill.contact.lastName].filter(Boolean).join(' ')
            || (prefill.contact && prefill.contact.businessName) || '')
        : '';

    const handlePickSide = (side) => {
        const next = resolvePrefillForSide(prefill, side);
        setActivePrefill(next);
        setTransactionType(next.transactionType);
        setFinancingType(next.financingType);
        setBudget(next.budget != null ? next.budget : '');
        setPropertyStreet(next.propertyStreet || '');
        setPropertyCityStateZip(next.propertyCityStateZip || '');
        setListPrice(next.listPrice != null ? next.listPrice : '');
    };

    // The type toggle answers the same question as the side pick. For a side
    // the contact has criteria for, it fills that side's fields from the
    // criteria (D-42), but only fields still empty, so an edit made before a
    // round trip on the toggle is kept. Fields of the other side are left as
    // they are, and the submit only reads the fields of the chosen type.
    const handleTypeChange = (type) => {
        setTransactionType(type);
        const side = type === 'Seller' ? 'seller' : type === 'Buyer' ? 'buyer' : null;
        if (!prefill || !side || !prefill.criteria || !prefill.criteria[side]) return;
        const next = resolvePrefillForSide(prefill, side);
        setActivePrefill(next);
        const isBlank = (v) => v === '' || v == null;
        if (side === 'seller') {
            setPropertyStreet((prev) => (isBlank(prev) ? next.propertyStreet || '' : prev));
            setPropertyCityStateZip((prev) => (isBlank(prev) ? next.propertyCityStateZip || '' : prev));
            setListPrice((prev) => (isBlank(prev) && next.listPrice != null ? next.listPrice : prev));
        } else {
            setBudget((prev) => (isBlank(prev) && next.budget != null ? next.budget : prev));
            setFinancingType((prev) => prev || next.financingType || '');
        }
    };

    const isCoClientChecked = (co) => clients.some((c) => c.contactId === co.client.contactId);
    const toggleCoClient = (co) => {
        setClients((prev) => (
            prev.some((c) => c.contactId === co.client.contactId)
                ? prev.filter((c) => c.contactId !== co.client.contactId)
                : [...prev, co.client]
        ));
    };
    const [isBusy, setIsBusy] = React.useState(false);

    // Animated pill indicator — same pattern as TransactionForm tab rail
    const toggleRailRef = React.useRef(null);
    const toggleButtonRefs = React.useRef(new Map());
    const [indicatorStyle, setIndicatorStyle] = React.useState({ opacity: 0, width: 0, x: 0 });

    const registerToggleButton = React.useCallback((value, node) => {
        if (node) {
            toggleButtonRefs.current.set(value, node);
        } else {
            toggleButtonRefs.current.delete(value);
        }
    }, []);

    React.useLayoutEffect(() => {
        const update = () => {
            const rail = toggleRailRef.current;
            const btn = toggleButtonRefs.current.get(transactionType);
            if (!rail || !btn) {
                setIndicatorStyle(prev => (prev.opacity === 0 ? prev : { opacity: 0, width: 0, x: 0 }));
                return;
            }
            const railRect = rail.getBoundingClientRect();
            const btnRect = btn.getBoundingClientRect();
            setIndicatorStyle({ opacity: 1, width: btnRect.width, x: btnRect.left - railRect.left });
        };
        update();
        window.addEventListener('resize', update);
        return () => window.removeEventListener('resize', update);
    }, [transactionType]);

    const handleCreate = async () => {
        setIsBusy(true);
        try {
            const payload = {
                transactionType,
                status,
                clients,
                financingType: shouldShowFinancing(transactionType) ? financingType : '',
            };
            if (prefill && transactionType === 'Seller') {
                const numericPrice = listPrice === '' || listPrice == null ? NaN : Number(listPrice);
                payload.propertyStreet = propertyStreet;
                payload.propertyCityStateZip = propertyCityStateZip;
                payload.listPrice = Number.isFinite(numericPrice) ? numericPrice : null;
            } else if (prefill) {
                const numericBudget = budget === '' || budget == null ? NaN : Number(budget);
                payload.budget = Number.isFinite(numericBudget) ? numericBudget : null;
                if (transactionType === 'Buyer' && financingType !== FINANCING_TYPES.CASH && Array.isArray(prefill.lenderIds) && prefill.lenderIds.length > 0) {
                    payload.lenderIds = prefill.lenderIds;
                }
            }
            await onStartTransaction(payload);
        } finally {
            setIsBusy(false);
        }
    };

    const overlayStyle = { backgroundColor: 'rgba(0,0,0,0.5)' };
    const containerClass = isMobile
        ? 'fixed inset-0 z-50 flex flex-col bg-[color:var(--surface-primary)]'
        : 'w-full max-w-3xl rounded-lg bg-[color:var(--surface-primary)] shadow-xl flex flex-col max-h-[90vh]';

    return (
        <div
            className={`fixed inset-0 z-50 flex ${isMobile ? '' : 'items-center justify-center p-4'}`}
            style={overlayStyle}
            role="dialog"
            aria-modal="true"
            aria-label={prefill ? 'Start a deal' : 'New Transaction'}
        >
            <div className={containerClass}>
                {/* Header */}
                <div className={`flex items-center justify-between border-b border-[color:var(--border-color)] flex-shrink-0 ${isMobile ? 'px-4 py-4' : 'px-6 py-5'}`}>
                    <h2 className="text-lg font-semibold text-[color:var(--text-primary)]">{prefill ? `Start a deal with ${prefillName}` : 'New Transaction'}</h2>
                    <button
                        type="button"
                        onClick={onCancel}
                        className="rounded-md p-1 text-[color:var(--text-faint)] hover:text-[color:var(--text-muted)] focus:outline-none"
                        aria-label="Close"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                {/* Content */}
                <div className={`flex-1 overflow-y-auto ${isMobile ? 'px-4 py-4' : 'px-6 py-5'} space-y-6`}>
                    {needsSidePick && (
                        <fieldset>
                            <legend className="block text-sm font-medium text-[color:var(--text-secondary)] mb-2">Are they buying or selling?</legend>
                            <div className="flex gap-4">
                                {[['buyer', 'Buying'], ['seller', 'Selling']].map(([side, label]) => (
                                    <div key={side} className="inline-flex items-center gap-2">
                                        <Input
                                            id={`start-deal-side-${side}`}
                                            type="radio"
                                            name="start-deal-side"
                                            value={side}
                                            checked={Boolean(activePrefill && activePrefill.side === side)}
                                            onChange={() => handlePickSide(side)}
                                            className="!w-4 !p-0 h-4"
                                        />
                                        <FieldLabel htmlFor={`start-deal-side-${side}`} className="text-[color:var(--text-primary)]">{label}</FieldLabel>
                                    </div>
                                ))}
                            </div>
                        </fieldset>
                    )}

                    {/* Client Type toggle */}
                    <div>
                        <label className="block text-sm font-medium text-[color:var(--text-secondary)] mb-2">Client Type</label>
                        <div
                            ref={toggleRailRef}
                            className="relative inline-flex gap-1 rounded-full border p-1 shadow-sm"
                            style={{ borderColor: 'var(--border-color)', backgroundColor: 'var(--surface-primary)' }}
                            role="group"
                            aria-label="Client type"
                        >
                            <span
                                className="pointer-events-none absolute inset-y-1 left-0 rounded-full transition-all duration-200 ease-out"
                                style={{
                                    backgroundColor: 'var(--color-accent)',
                                    boxShadow: '0 6px 16px rgba(15, 23, 42, 0.18)',
                                    opacity: indicatorStyle.opacity,
                                    width: `${indicatorStyle.width}px`,
                                    transform: `translateX(${indicatorStyle.x}px)`
                                }}
                                aria-hidden="true"
                            />
                            {TYPE_OPTIONS.map((type) => {
                                const isActive = transactionType === type;
                                return (
                                    <button
                                        key={type}
                                        ref={(node) => registerToggleButton(type, node)}
                                        type="button"
                                        onClick={() => handleTypeChange(type)}
                                        className="relative z-10 inline-flex items-center rounded-full px-5 py-1.5 text-sm font-semibold transition-colors focus:outline-none"
                                        style={isActive ? { color: 'var(--on-accent)' } : { color: 'var(--text-muted)' }}
                                        aria-pressed={isActive}
                                    >
                                        {transactionTypeLabel(type)}
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    {/* Status */}
                    <div>
                        <label htmlFor="start-txn-status" className="block text-sm font-medium text-[color:var(--text-secondary)] mb-1">Status</label>
                        <select
                            id="start-txn-status"
                            value={status}
                            onChange={(e) => setStatus(e.target.value)}
                            className="w-full rounded-md border border-[color:var(--border-color)] p-2.5 text-sm text-[color:var(--text-primary)] focus:border-[color:var(--color-accent)] focus:outline-none focus:ring-1 focus:ring-[color:var(--color-accent)]"
                        >
                            {statusOptions.map((s) => (
                                <option key={s} value={s}>{s}</option>
                            ))}
                        </select>
                    </div>

                    {/* Financing — Buyer-only, required before create */}
                    {shouldShowFinancing(transactionType) && (
                        <div>
                            <label className="block text-sm font-medium text-[color:var(--text-secondary)] mb-2">
                                Financing
                            </label>
                            <FinancingToggle
                                value={financingType}
                                onChange={setFinancingType}
                                ariaLabel="Financing type"
                            />
                            <p className="mt-2 text-xs text-[color:var(--text-muted)]">
                                Cash buyers skip lender, Intent to Proceed, and homeowners insurance.
                            </p>
                            {prefill && financingType && financingType === activePrefill.financingType && (
                                <p className="mt-1 text-xs text-[color:var(--text-muted)]">From {prefill.firstName}'s criteria</p>
                            )}
                        </div>
                    )}

                    {prefill && transactionType === 'Seller' && (
                        <div className="space-y-3">
                            <div>
                                <FieldLabel htmlFor="start-deal-street">Street address</FieldLabel>
                                <Input
                                    id="start-deal-street"
                                    name="propertyStreet"
                                    autoComplete="street-address"
                                    value={propertyStreet}
                                    onChange={(e) => setPropertyStreet(e.target.value)}
                                />
                                {activePrefill.propertyStreet && propertyStreet === activePrefill.propertyStreet && (
                                    <p className="mt-1 text-xs text-[color:var(--text-muted)]">From {prefill.firstName}'s criteria</p>
                                )}
                            </div>
                            <div>
                                <FieldLabel htmlFor="start-deal-city-state-zip">City, state, ZIP</FieldLabel>
                                <Input
                                    id="start-deal-city-state-zip"
                                    name="propertyCityStateZip"
                                    autoComplete="off"
                                    value={propertyCityStateZip}
                                    onChange={(e) => setPropertyCityStateZip(e.target.value)}
                                />
                                {activePrefill.propertyCityStateZip && propertyCityStateZip === activePrefill.propertyCityStateZip && (
                                    <p className="mt-1 text-xs text-[color:var(--text-muted)]">From {prefill.firstName}'s criteria</p>
                                )}
                            </div>
                            <div>
                                <CurrencyInput
                                    label="List price"
                                    id="start-deal-list-price"
                                    inputMode="numeric"
                                    name="listPrice"
                                    value={listPrice}
                                    onChange={(e) => setListPrice(e.target.value)}
                                    allowDecimals={false}
                                />
                                {activePrefill.listPrice != null && Number(listPrice) === activePrefill.listPrice && (
                                    <p className="mt-1 text-xs text-[color:var(--text-muted)]">From {prefill.firstName}'s criteria</p>
                                )}
                            </div>
                        </div>
                    )}

                    {prefill && transactionType !== 'Seller' && (
                        <div>
                            <CurrencyInput
                                label="Budget"
                                id="start-deal-budget"
                                inputMode="numeric"
                                name="budget"
                                value={budget}
                                onChange={(e) => setBudget(e.target.value)}
                                allowDecimals={false}
                            />
                            {activePrefill.budget != null && Number(budget) === activePrefill.budget && (
                                <p className="mt-1 text-xs text-[color:var(--text-muted)]">From {prefill.firstName}'s criteria</p>
                            )}
                        </div>
                    )}

                    {prefill && (prefill.coClients || []).length > 0 && (
                        <div>
                            <p className="block text-sm font-medium text-[color:var(--text-secondary)] mb-2">Also on this deal</p>
                            <div className="space-y-2">
                                {prefill.coClients.map((co) => {
                                    const name = [co.contact.firstName, co.contact.lastName].filter(Boolean).join(' ') || co.contact.businessName || 'Another contact';
                                    return (
                                        <div key={co.client.contactId} className="flex items-center gap-2">
                                            <Input
                                                id={`start-deal-co-${co.client.contactId}`}
                                                type="checkbox"
                                                checked={isCoClientChecked(co)}
                                                onChange={() => toggleCoClient(co)}
                                                className="!w-4 !p-0 h-4"
                                            />
                                            <FieldLabel htmlFor={`start-deal-co-${co.client.contactId}`} className="text-[color:var(--text-primary)]">
                                                {name} was on the same prospect deal
                                            </FieldLabel>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {/* Clients */}
                    <div>
                        <label className="block text-sm font-medium text-[color:var(--text-secondary)] mb-2">Client(s)</label>
                        <ClientsSection
                            clients={clients}
                            onClientsChange={setClients}
                            contacts={contacts}
                            onUpdateContact={onUpdateContact}
                            onCreateContact={onCreateContact}
                            onSyncContactFromGoogle={onSyncContactFromGoogle}
                            applyReviewedSync={applyReviewedSync}
                            applyResolvedSync={applyResolvedSync}
                            onSearchGoogleContacts={onSearchGoogleContacts}
                            workspaceId={workspaceId}
                            user={user}
                            mapClientWithId={mapClientWithId}
                            sanitizeClientHelpers={sanitizeClientHelpers}
                        />
                    </div>
                </div>

                {/* Footer */}
                <div className={`flex-shrink-0 border-t border-[color:var(--border-color)] ${isMobile ? 'px-4 py-4' : 'px-6 py-4'}`}>
                    <div className={`flex gap-3 ${isMobile ? 'flex-col-reverse' : 'justify-end'}`}>
                        <button
                            type="button"
                            onClick={onCancel}
                            disabled={isBusy}
                            className={`rounded-md border border-[color:var(--border-color)] px-4 py-2 text-sm font-semibold text-[color:var(--text-secondary)] hover:bg-[color:var(--surface-subtle)] disabled:opacity-50 focus:outline-none ${isMobile ? 'w-full' : ''}`}
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            onClick={handleCreate}
                            disabled={isBusy || needsSidePick || (isFinancingChoiceRequired(transactionType) && !financingType)}
                            className={`inline-flex items-center justify-center gap-2 rounded-md px-4 py-2 text-sm font-semibold text-[color:var(--on-accent)] disabled:opacity-60 hover:opacity-90 transition-opacity focus:outline-none ${isMobile ? 'w-full' : ''}`}
                            style={{ backgroundColor: 'var(--color-accent)' }}
                        >
                            {isBusy && (
                                <svg className="h-4 w-4 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                                </svg>
                            )}
                            {prefill ? 'Start deal' : 'Create Transaction'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default StartTransactionModal;
